import React, { memo, useEffect, useMemo, useState } from "react";
import { Dimensions, Image, Platform } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withSpring } from "react-native-reanimated";
import { useMediaImage } from "@lookiero/sty-psp-ui";
import { ItemStatus } from "../../itemStatus";
import { style as cardStyle, WIDTH } from "./Card.style";

const SPRING_CONFIG = {
  damping: 25,
  mass: 1,
  stiffness: 350 - 0.125 * Dimensions.get("window").width,
};

interface CardProps {
  readonly imageUrl: string;
  readonly activeIndex: number;
  readonly index: number;
  readonly status: ItemStatus | undefined;
  readonly onStatusChanged: (status: ItemStatus | undefined) => void;
  readonly onStatusChanging: (status: ItemStatus | undefined) => void;
}

const Card: React.FC<CardProps> = ({ imageUrl, activeIndex, index, status, onStatusChanged, onStatusChanging }) => {
  const [outOfWindowTranslationX, setOutOfWindowTranslationX] = useState(Dimensions.get("window").width + 60);
  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) =>
      setOutOfWindowTranslationX(window.width + 60),
    );

    return () => subscription?.remove();
  });

  const panActionThreshold = Platform.OS === "web" ? WIDTH / 3 : WIDTH / 4;
  const cdnImageUrl = useMediaImage();

  const style = useMemo(() => cardStyle({ index }), [index]);

  const showActiveShadow = activeIndex >= index;
  const showBaseShadow = !showActiveShadow && Math.abs(activeIndex - index) < 3;

  const panTranslationX = useSharedValue(0);
  const panTranslationY = useSharedValue(0);
  const panInitialTranslationX = useSharedValue<number | undefined>(undefined);
  const panInitialTranslationY = useSharedValue<number | undefined>(undefined);
  const panGesture = useMemo(
    () =>
      Gesture.Pan()
        .withTestId("card-pan-gesture")
        .enabled(activeIndex === index && status === undefined)
        .maxPointers(1)
        .activeOffsetX([-20, 20])
        .onBegin(() => {
          if (Platform.OS !== "web") {
            return;
          }

          panInitialTranslationX.value = panTranslationX.value;
          panInitialTranslationY.value = panTranslationY.value;
        })
        .onStart(() => {
          if (Platform.OS === "web") {
            return;
          }

          panInitialTranslationX.value = panTranslationX.value;
          panInitialTranslationY.value = panTranslationY.value;
        })
        .onUpdate(({ translationX, translationY }) => {
          if (panInitialTranslationX.value === undefined || panInitialTranslationY.value === undefined) {
            return;
          }

          const tX = panInitialTranslationX.value + translationX;
          const tY = panInitialTranslationY.value + translationY;
          panTranslationX.value = tX;
          panTranslationY.value = tY;

          const status =
            Math.abs(tX) >= panActionThreshold
              ? translationX > 0
                ? ItemStatus.LIKED
                : ItemStatus.DISLIKED
              : undefined;

          runOnJS(onStatusChanging)(status);
        })
        .onEnd(({ translationX }) => {
          if (panInitialTranslationX.value === undefined || panInitialTranslationY.value === undefined) {
            return;
          }

          const tX = panInitialTranslationX.value + translationX;
          const status =
            Math.abs(tX) >= panActionThreshold
              ? translationX > 0
                ? ItemStatus.LIKED
                : ItemStatus.DISLIKED
              : undefined;

          const finalX =
            status === undefined ? 0 : status === ItemStatus.LIKED ? outOfWindowTranslationX : -outOfWindowTranslationX;
          panTranslationX.value = withSpring(finalX, SPRING_CONFIG);
          panTranslationY.value = withSpring(0, SPRING_CONFIG);
          panInitialTranslationX.value = undefined;
          panInitialTranslationY.value = undefined;

          runOnJS(onStatusChanged)(status);
          runOnJS(onStatusChanging)(undefined);
        }),
    [
      activeIndex,
      index,
      onStatusChanged,
      onStatusChanging,
      outOfWindowTranslationX,
      panActionThreshold,
      panInitialTranslationX,
      panInitialTranslationY,
      panTranslationX,
      panTranslationY,
      status,
    ],
  );

  const animatedStyle = useAnimatedStyle(
    () => ({
      transform: [
        {
          translateX:
            status === undefined
              ? panTranslationX.value
              : withSpring(
                  status === ItemStatus.LIKED ? outOfWindowTranslationX : -outOfWindowTranslationX,
                  SPRING_CONFIG,
                ),
        },
        {
          translateY: panTranslationY.value,
        },
        {
          rotate:
            status === undefined
              ? index === activeIndex
                ? `${panTranslationX.value / 30}deg`
                : withSpring(
                    index - activeIndex === 1 ? "4deg" : index - activeIndex === 2 ? "-4deg" : "0deg",
                    SPRING_CONFIG,
                  )
              : withSpring(
                  status === ItemStatus.LIKED
                    ? `${outOfWindowTranslationX / 30}deg`
                    : `${-outOfWindowTranslationX / 30}deg`,
                  SPRING_CONFIG,
                ),
        },
      ],
    }),
    [activeIndex, index, outOfWindowTranslationX, panTranslationX, panTranslationY, status],
  );

  return (
    <GestureDetector gesture={panGesture}>
      <Animated.View
        style={[style.card, showActiveShadow && style.activeShadow, showBaseShadow && style.baseShadow, animatedStyle]}
      >
        <Image resizeMode="cover" source={{ uri: cdnImageUrl({ url: imageUrl, width: WIDTH }) }} style={style.image} />
      </Animated.View>
    </GestureDetector>
  );
};

const MemoizedCard = memo(Card);
export { MemoizedCard as Card };
